import { useState, useEffect, useCallback } from "react";
import { C, S, BASE } from "../constants";
import Img from "../components/Img";
import ProdLink from "../components/ProdLink";
import { exportXlsx } from "../utils";

const API = BASE;
const token = () => localStorage.getItem("crm_token");
const fmt = (n) => n?.toLocaleString("uk") ?? "—";
const fmtUah = (n) => (n != null ? n.toLocaleString("uk", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₴" : "—");

function apiFetch(url, opts = {}) {
  return fetch(API + url, { ...opts, headers: { Authorization: "Bearer " + token(), "Content-Type": "application/json", ...(opts.headers || {}) } });
}

// ── Shared ─────────────────────────────────────────────────────────────────

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          ...S.btn(active === t.id),
          fontSize: 13, padding: "7px 16px",
        }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px", color: C.text3, fontSize: 14 }}>
      {text}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{ ...S.card, minWidth: 340, maxWidth: 560, width: "90%", maxHeight: "90vh", overflow: "auto" }}
           onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text1 }}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.text3, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      {label && <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</div>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
      />
    </div>
  );
}

function PrimaryBtn({ onClick, disabled, children, color = C.blue }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "9px 20px", borderRadius: 9, fontSize: 13, fontWeight: 600,
      background: disabled ? `${color}40` : color,
      color: disabled ? `${color}80` : "#fff",
      border: "none", cursor: disabled ? "default" : "pointer",
      transition: "all .15s",
    }}>{children}</button>
  );
}

// ── Stock tab ──────────────────────────────────────────────────────────────

function StockTab() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [adjusting, setAdjusting] = useState(null); // product being adjusted
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustWh, setAdjustWh] = useState("");
  const [sort, setSort] = useState("total");
  const [order, setOrder] = useState("desc");

  const PAGE_SIZE = 50;

  const load = useCallback(async (p = 1, s = "", srt = "total", ord = "desc") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE, sort: srt, order: ord });
      if (s) params.set("search", s);
      const res = await apiFetch(`/api/warehouse/stock?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.items);
        setTotal(data.total);
        setWarehouses(data.warehouses || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(1, search, sort, order); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  useEffect(() => { load(page, search, sort, order); }, [page]); // eslint-disable-line

  useEffect(() => { setPage(1); load(1, search, sort, order); }, [sort, order]); // eslint-disable-line

  const toggleSort = (key) => {
    if (sort === key) { setOrder(o => o === "asc" ? "desc" : "asc"); }
    else { setSort(key); setOrder("desc"); }
  };
  const sortArrow = (key) => sort === key ? (order === "asc" ? " ▲" : " ▼") : "";

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const openAdjust = (p) => {
    setAdjusting(p);
    const def = warehouses.find(w => w.is_default_fbs) || warehouses[0];
    setAdjustWh(def ? String(def.id) : "");
  };

  const doAdjust = async () => {
    const delta = parseInt(adjustDelta, 10);
    if (!delta || !adjusting) return;
    await apiFetch("/api/warehouse/stock/adjust", {
      method: "POST",
      body: JSON.stringify({
        product_id: adjusting.id,
        delta,
        warehouse_id: adjustWh ? parseInt(adjustWh, 10) : undefined,
        note: adjustNote || undefined,
      }),
    });
    setAdjusting(null);
    setAdjustDelta("");
    setAdjustNote("");
    load(page, search, sort, order);
  };

  const stockBadge = (n) => {
    if (n <= 0) return <span style={{ ...S.badge(C.red), fontSize: 11 }}>0</span>;
    if (n <= 3) return <span style={{ ...S.badge(C.yellow), fontSize: 11 }}>{n}</span>;
    return <span style={{ ...S.badge(C.green), fontSize: 11 }}>{n}</span>;
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Пошук товару..."
          style={{ ...S.input, flex: 1 }}
        />
        <span style={{ color: C.text3, fontSize: 12 }}>{total} товарів</span>
      </div>

      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...S.th(), paddingLeft: 16, cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("name")}>Товар{sortArrow("name")}</th>
              {warehouses.map(w => (
                <th key={w.id} style={{ ...S.th(), textAlign: "right" }} title={w.name}>
                  {w.is_default_fbs ? "★ " : ""}{w.name.length > 14 ? w.name.slice(0, 13) + "…" : w.name}
                </th>
              ))}
              <th style={{ ...S.th(), textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("ff")}>FF{sortArrow("ff")}</th>
              <th style={{ ...S.th(), textAlign: "right" }}>В дорозі</th>
              <th style={{ ...S.th(), textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("total")}>Всього{sortArrow("total")}</th>
              <th style={{ ...S.th(), textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("cost")}>Собівартість{sortArrow("cost")}</th>
              <th style={{ ...S.th() }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={warehouses.length + 6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>Завантаження...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={warehouses.length + 6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>
                {search ? "Нічого не знайдено" : "Немає товарів"}
              </td></tr>
            )}
            {items.map(p => (
              <tr key={p.id}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ ...S.td, paddingLeft: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {p.image_url && <Img src={p.image_url} style={{ width: 36, height: 36, objectFit: "contain", borderRadius: 6, flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      <ProdLink name={p.name} productId={p.rozetka_id} style={{ fontSize: 13, maxWidth: 280 }} />
                      {p.article && <div style={{ fontSize: 11, color: C.text3 }}>{p.article}</div>}
                    </div>
                  </div>
                </td>
                {warehouses.map(w => (
                  <td key={w.id} style={{ ...S.td, textAlign: "right" }}>
                    {stockBadge((p.by_warehouse && p.by_warehouse[String(w.id)]) || 0)}
                  </td>
                ))}
                <td style={{ ...S.td, textAlign: "right" }}>
                  {p.is_ff
                    ? <span style={{ ...S.badge(C.blue), fontSize: 11 }}>{p.ff_stock}</span>
                    : <span style={{ color: C.text4, fontSize: 12 }}>—</span>}
                </td>
                <td style={{ ...S.td, textAlign: "right" }}>
                  {p.incoming > 0
                    ? <span style={{ ...S.badge(C.blue), fontSize: 11 }} title="Замовлено, в дорозі">🚚 {p.incoming}</span>
                    : <span style={{ color: C.text4, fontSize: 12 }}>—</span>}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontSize: 13, fontWeight: 700, color: C.text1 }}>{fmt(p.total_stock)}</td>
                <td style={{ ...S.td, textAlign: "right", fontSize: 13, color: p.cost_price > 0 ? C.text1 : C.text4 }}>
                  {p.cost_price > 0 ? fmtUah(p.cost_price) : "—"}
                </td>
                <td style={{ ...S.td, textAlign: "right", paddingRight: 12 }}>
                  <button onClick={() => openAdjust(p)} style={{
                    ...S.btn(false), padding: "4px 10px", fontSize: 11,
                  }}>±</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={S.btn(false)}>← Назад</button>
          <span style={{ color: C.text3, fontSize: 13, alignSelf: "center" }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={S.btn(false)}>Вперед →</button>
        </div>
      )}

      {adjusting && (
        <Modal title={`Коригування: ${adjusting.name?.slice(0, 40)}`} onClose={() => { setAdjusting(null); setAdjustDelta(""); setAdjustNote(""); }}>
          <div style={{ marginBottom: 10, fontSize: 13, color: C.text2 }}>
            Свій запас (усього): <strong style={{ color: C.text1 }}>{adjusting.own_stock}</strong>
          </div>
          {warehouses.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Склад</div>
              <select value={adjustWh} onChange={e => setAdjustWh(e.target.value)} style={{ ...S.input, width: "100%", boxSizing: "border-box" }}>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.name}{w.is_default_fbs ? " ★" : ""} — {(adjusting.by_warehouse && adjusting.by_warehouse[String(w.id)]) || 0} од.
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input label="Зміна (+ або -)" type="number" value={adjustDelta} onChange={setAdjustDelta} placeholder="+5 або -2" />
          <Input label="Примітка" value={adjustNote} onChange={setAdjustNote} placeholder="Наприклад: списання браку" />
          {adjustDelta && parseInt(adjustDelta, 10) !== 0 && (
            <div style={{ fontSize: 13, color: C.text3, marginBottom: 14 }}>
              Нове значення на складі: <strong style={{ color: parseInt(adjustDelta, 10) > 0 ? C.green : C.yellow }}>
                {Math.max(0, ((adjusting.by_warehouse && adjustWh && adjusting.by_warehouse[adjustWh]) || 0) + parseInt(adjustDelta, 10))}
              </strong>
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={doAdjust} disabled={!adjustDelta || parseInt(adjustDelta, 10) === 0}>Зберегти</PrimaryBtn>
            <button onClick={() => { setAdjusting(null); setAdjustDelta(""); setAdjustNote(""); }}
                    style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Purchases tab ──────────────────────────────────────────────────────────

function PurchasesTab() {
  const [purchases, setPurchases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const PAGE_SIZE = 20;

  // New purchase form state
  const [form, setForm] = useState({ supplier_id: "", currency: "UAH", exchange_rate: "1", note: "", items: [] });
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/warehouse/purchases?page=${p}&page_size=${PAGE_SIZE}`);
      if (res.ok) { const d = await res.json(); setPurchases(d.items); setTotal(d.total); }
    } finally { setLoading(false); }
  }, []);

  const loadSuppliers = useCallback(async () => {
    const res = await apiFetch("/api/warehouse/suppliers");
    if (res.ok) setSuppliers(await res.json());
  }, []);

  useEffect(() => { load(1); loadSuppliers(); }, []); // eslint-disable-line
  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  const searchProducts = async (q) => {
    if (!q || q.length < 2) { setProductResults([]); return; }
    setProductSearchLoading(true);
    try {
      const res = await apiFetch(`/api/warehouse/stock?search=${encodeURIComponent(q)}&page_size=10`);
      if (res.ok) { const d = await res.json(); setProductResults(d.items); }
    } finally { setProductSearchLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => searchProducts(productSearch), 350);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addItem = (prod) => {
    if (form.items.find(i => i.product_id === prod.id)) return;
    setForm(f => ({ ...f, items: [...f.items, { product_id: prod.id, name: prod.name, image_url: prod.image_url, qty: 1, unit_price_foreign: "" }] }));
    setProductSearch("");
    setProductResults([]);
  };

  const removeItem = (productId) => setForm(f => ({ ...f, items: f.items.filter(i => i.product_id !== productId) }));

  const updateItem = (productId, field, value) => {
    setForm(f => ({ ...f, items: f.items.map(i => i.product_id === productId ? { ...i, [field]: value } : i) }));
  };

  const submitPurchase = async () => {
    const rate = parseFloat(form.exchange_rate) || 1;
    const payload = {
      supplier_id: parseInt(form.supplier_id),
      currency: form.currency,
      exchange_rate: rate,
      note: form.note || undefined,
      items: form.items.map(i => ({
        product_id: i.product_id,
        qty: parseInt(i.qty),
        unit_price_foreign: parseFloat(i.unit_price_foreign),
      })),
    };
    const res = await apiFetch("/api/warehouse/purchases", { method: "POST", body: JSON.stringify(payload) });
    if (res.ok) {
      setCreating(false);
      setForm({ supplier_id: "", currency: "UAH", exchange_rate: "1", note: "", items: [] });
      load(1);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || "Помилка при створенні");
    }
  };

  const receivePurchase = async (id) => {
    if (!window.confirm("Позначити надходження як отримане? Це оновить запаси та собівартість.")) return;
    const res = await apiFetch(`/api/warehouse/purchases/${id}/receive`, { method: "POST" });
    if (res.ok) { load(page); if (viewingId === id) loadView(id); }
    else { const err = await res.json().catch(() => ({})); alert(err.detail || "Помилка"); }
  };

  const orderPurchase = async (id) => {
    const eta = window.prompt("Очікувана дата прибуття (РРРР-ММ-ДД), можна лишити порожнім:", "");
    if (eta === null) return;
    const res = await apiFetch(`/api/warehouse/purchases/${id}/order`, {
      method: "POST", body: JSON.stringify({ eta: eta.trim() || undefined }),
    });
    if (res.ok) { load(page); if (viewingId === id) loadView(id); }
    else { const err = await res.json().catch(() => ({})); alert(err.detail || "Помилка"); }
  };

  const statusBadge = (st) => {
    if (st === "received") return <span style={S.badge(C.green)}>Отримано</span>;
    if (st === "in_transit") return <span style={S.badge(C.blue)}>🚚 В дорозі</span>;
    return <span style={S.badge(C.yellow)}>Чернетка</span>;
  };

  const deletePurchase = async (id) => {
    if (!window.confirm("Видалити чернетку надходження?")) return;
    const res = await apiFetch(`/api/warehouse/purchases/${id}`, { method: "DELETE" });
    if (res.ok) load(page);
  };

  const loadView = async (id) => {
    setViewingId(id);
    const res = await apiFetch(`/api/warehouse/purchases/${id}`);
    if (res.ok) setViewData(await res.json());
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const canSubmit = form.supplier_id && form.items.length > 0 &&
    form.items.every(i => i.qty > 0 && parseFloat(i.unit_price_foreign) > 0);

  const selectedCurrency = form.currency || "UAH";
  const rate = parseFloat(form.exchange_rate) || 1;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ color: C.text3, fontSize: 13 }}>Всього {total} надходжень</span>
        <PrimaryBtn onClick={() => setCreating(true)}>+ Нове надходження</PrimaryBtn>
      </div>

      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={S.th()}>№</th>
              <th style={S.th()}>Постачальник</th>
              <th style={S.th()}>Дата</th>
              <th style={{ ...S.th(), textAlign: "right" }}>Сума</th>
              <th style={{ ...S.th(), textAlign: "center" }}>Статус</th>
              <th style={S.th()} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>Завантаження...</td></tr>}
            {!loading && purchases.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>Немає надходжень</td></tr>}
            {purchases.map(p => (
              <tr key={p.id}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onClick={() => loadView(p.id)}>
                <td style={{ ...S.td, paddingLeft: 16, color: C.text3, fontSize: 12 }}>#{p.id}</td>
                <td style={{ ...S.td, fontSize: 13, color: C.text1 }}>{p.supplier_name || "—"}</td>
                <td style={{ ...S.td, fontSize: 12, color: C.text3 }}>
                  {p.created_at ? new Date(p.created_at).toLocaleDateString("uk") : "—"}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontSize: 13, color: C.text1 }}>{fmtUah(p.total_uah)}</td>
                <td style={{ ...S.td, textAlign: "center" }}>
                  {statusBadge(p.status)}
                  {p.status === "in_transit" && p.eta && (
                    <div style={{ fontSize: 10, color: C.text4, marginTop: 2 }}>≈ {p.eta}</div>
                  )}
                </td>
                <td style={{ ...S.td, textAlign: "right", paddingRight: 12 }} onClick={e => e.stopPropagation()}>
                  {p.status !== "received" && (
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {p.status === "draft" && (
                        <button onClick={() => orderPurchase(p.id)} style={{ ...S.btn(false, C.blue), padding: "4px 10px", fontSize: 11 }}>🚚 Замовлено</button>
                      )}
                      <button onClick={() => receivePurchase(p.id)} style={{ ...S.btn(false, C.green), padding: "4px 10px", fontSize: 11 }}>✓ Отримати</button>
                      <button onClick={() => deletePurchase(p.id)} style={{ ...S.btn(false, C.red), padding: "4px 10px", fontSize: 11 }}>✕</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={S.btn(false)}>← Назад</button>
          <span style={{ color: C.text3, fontSize: 13, alignSelf: "center" }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={S.btn(false)}>Вперед →</button>
        </div>
      )}

      {/* View purchase modal */}
      {viewingId && viewData && (
        <Modal title={`Надходження #${viewData.id}`} onClose={() => { setViewingId(null); setViewData(null); }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: C.text2, display: "flex", gap: 16, flexWrap: "wrap" }}>
            <span>Постачальник: <strong style={{ color: C.text1 }}>{viewData.supplier_name}</strong></span>
            <span>Валюта: <strong style={{ color: C.text1 }}>{viewData.currency}</strong>
              {viewData.currency !== "UAH" && <span style={{ color: C.text3 }}> × {viewData.exchange_rate}</span>}
            </span>
            {statusBadge(viewData.status)}
            {viewData.status === "in_transit" && viewData.eta && (
              <span style={{ color: C.text3 }}>≈ прибуття {viewData.eta}</span>
            )}
          </div>
          {viewData.note && <div style={{ fontSize: 12, color: C.text3, marginBottom: 12 }}>{viewData.note}</div>}
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th()}>Товар</th>
                <th style={{ ...S.th(), textAlign: "right" }}>К-сть</th>
                <th style={{ ...S.th(), textAlign: "right" }}>Ціна ({viewData.currency})</th>
                <th style={{ ...S.th(), textAlign: "right" }}>Сума (₴)</th>
              </tr>
            </thead>
            <tbody>
              {viewData.items.map(i => (
                <tr key={i.id}>
                  <td style={S.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {i.image_url && <Img src={i.image_url} style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }} />}
                      <span style={{ fontSize: 12, color: C.text1 }}>{i.product_name}</span>
                    </div>
                  </td>
                  <td style={{ ...S.td, textAlign: "right", fontSize: 13 }}>{i.qty}</td>
                  <td style={{ ...S.td, textAlign: "right", fontSize: 13 }}>{i.unit_price_foreign?.toLocaleString("uk")}</td>
                  <td style={{ ...S.td, textAlign: "right", fontSize: 13 }}>{fmtUah(i.unit_price_uah * i.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {viewData.status !== "received" && (
            <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
              {viewData.status === "draft" && (
                <PrimaryBtn onClick={() => { orderPurchase(viewData.id); }} color={C.blue}>🚚 Замовлено (в дорозі)</PrimaryBtn>
              )}
              <PrimaryBtn onClick={() => { receivePurchase(viewData.id); }} color={C.green}>✓ Позначити як отримане</PrimaryBtn>
            </div>
          )}
        </Modal>
      )}

      {/* Create purchase modal */}
      {creating && (
        <Modal title="Нове надходження" onClose={() => { setCreating(false); setForm({ supplier_id: "", currency: "UAH", exchange_rate: "1", note: "", items: [] }); }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <div style={{ marginBottom: 14, gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Постачальник</div>
              <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}
                      style={{ ...S.input, width: "100%" }}>
                <option value="">Оберіть постачальника...</option>
                {suppliers.filter(s => s.is_active).map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.currency})</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Валюта</div>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value, exchange_rate: e.target.value === "UAH" ? "1" : f.exchange_rate }))}
                      style={{ ...S.input, width: "100%" }}>
                {["UAH", "USD", "EUR", "CNY", "PLN"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Input label={`Курс (1 ${selectedCurrency} = ? ₴)`} type="number" value={form.exchange_rate}
                   onChange={v => setForm(f => ({ ...f, exchange_rate: v }))} placeholder="1"
                   style={{ opacity: form.currency === "UAH" ? 0.4 : 1, marginBottom: 0 }} />
          </div>
          <Input label="Примітка (необов'язково)" value={form.note} onChange={v => setForm(f => ({ ...f, note: v }))} placeholder="Рахунок-фактура #123" />

          <div style={{ marginBottom: 8, fontSize: 11, color: C.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Товари</div>

          {/* Product search */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input value={productSearch} onChange={e => setProductSearch(e.target.value)}
                   placeholder="Пошук товару для додавання..."
                   style={{ ...S.input, width: "100%", boxSizing: "border-box" }} />
            {productResults.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 8,
                boxShadow: "0 8px 32px rgba(0,0,0,.5)", maxHeight: 200, overflow: "auto",
              }}>
                {productSearchLoading && <div style={{ padding: "10px 14px", color: C.text3, fontSize: 12 }}>Пошук...</div>}
                {productResults.map(p => (
                  <div key={p.id} onClick={() => addItem(p)} style={{
                    padding: "8px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {p.image_url && <Img src={p.image_url} style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 4 }} />}
                    <span style={{ fontSize: 12, color: C.text1 }}>{p.name}</span>
                    <span style={{ fontSize: 11, color: C.text3, marginLeft: "auto" }}>запас: {p.own_stock}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Items list */}
          {form.items.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {form.items.map(item => (
                <div key={item.product_id} style={{
                  display: "grid", gridTemplateColumns: "1fr 70px 100px 24px", gap: 8,
                  alignItems: "center", marginBottom: 8,
                }}>
                  <div style={{ fontSize: 12, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </div>
                  <input type="number" value={item.qty} min="1" placeholder="к-сть"
                         onChange={e => updateItem(item.product_id, "qty", parseInt(e.target.value) || 1)}
                         style={{ ...S.input, padding: "6px 8px", textAlign: "center" }} />
                  <div style={{ position: "relative" }}>
                    <input type="number" value={item.unit_price_foreign} placeholder="ціна" step="0.01"
                           onChange={e => updateItem(item.product_id, "unit_price_foreign", e.target.value)}
                           style={{ ...S.input, padding: "6px 8px", paddingRight: 28, width: "100%", boxSizing: "border-box" }} />
                    <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: C.text4 }}>{selectedCurrency}</span>
                  </div>
                  <button onClick={() => removeItem(item.product_id)}
                          style={{ background: "none", border: "none", color: C.text3, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
                </div>
              ))}
              {/* Total */}
              <div style={{ textAlign: "right", fontSize: 13, color: C.text2, marginTop: 8 }}>
                Разом: <strong style={{ color: C.text1 }}>
                  {fmtUah(form.items.reduce((s, i) => s + (parseFloat(i.unit_price_foreign) || 0) * (parseInt(i.qty) || 0) * rate, 0))}
                </strong>
              </div>
            </div>
          )}

          {form.items.length === 0 && (
            <div style={{ textAlign: "center", color: C.text4, fontSize: 12, padding: "16px 0", marginBottom: 14 }}>
              Додайте товари через пошук вище
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={submitPurchase} disabled={!canSubmit}>Створити чернетку</PrimaryBtn>
            <button onClick={() => { setCreating(false); setForm({ supplier_id: "", currency: "UAH", exchange_rate: "1", note: "", items: [] }); }}
                    style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Suppliers tab ──────────────────────────────────────────────────────────

function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", currency: "UAH", contact_info: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/warehouse/suppliers");
      if (res.ok) setSuppliers(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) {
      await apiFetch(`/api/warehouse/suppliers/${editing.id}`, { method: "PUT", body: JSON.stringify(form) });
    } else {
      await apiFetch("/api/warehouse/suppliers", { method: "POST", body: JSON.stringify(form) });
    }
    setCreating(false);
    setEditing(null);
    setForm({ name: "", currency: "UAH", contact_info: "" });
    load();
  };

  const deactivate = async (s) => {
    if (!window.confirm(`Деактивувати "${s.name}"?`)) return;
    await apiFetch(`/api/warehouse/suppliers/${s.id}`, { method: "PUT", body: JSON.stringify({ is_active: false }) });
    load();
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, currency: s.currency, contact_info: s.contact_info || "" });
    setCreating(true);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <PrimaryBtn onClick={() => { setEditing(null); setForm({ name: "", currency: "UAH", contact_info: "" }); setCreating(true); }}>
          + Додати постачальника
        </PrimaryBtn>
      </div>

      {loading && <EmptyState text="Завантаження..." />}
      {!loading && suppliers.length === 0 && <EmptyState text="Немає постачальників. Додайте першого." />}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {suppliers.map(s => (
          <div key={s.id} style={{
            ...S.card, display: "flex", alignItems: "center", gap: 16,
            opacity: s.is_active ? 1 : 0.5, padding: "14px 18px",
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: `${C.blue}18`,
              border: `1px solid ${C.blue}30`, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 18, flexShrink: 0,
            }}>🏭</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text1 }}>{s.name}</div>
              <div style={{ fontSize: 12, color: C.text3, marginTop: 2 }}>
                {s.currency}
                {s.contact_info && <span style={{ marginLeft: 10 }}>{s.contact_info}</span>}
              </div>
            </div>
            {!s.is_active && <span style={S.badge(C.text4)}>Неактивний</span>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => openEdit(s)} style={{ ...S.btn(false), padding: "5px 12px", fontSize: 12 }}>Редагувати</button>
              {s.is_active && <button onClick={() => deactivate(s)} style={{ ...S.btn(false, C.red), padding: "5px 12px", fontSize: 12 }}>Деактивувати</button>}
            </div>
          </div>
        ))}
      </div>

      {creating && (
        <Modal title={editing ? "Редагувати постачальника" : "Новий постачальник"} onClose={() => { setCreating(false); setEditing(null); }}>
          <Input label="Назва" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="ТОВ Ромашка" />
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.5px" }}>Валюта</div>
            <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                    style={{ ...S.input, width: "100%" }}>
              {["UAH", "USD", "EUR", "CNY", "PLN"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Input label="Контакт (необов'язково)" value={form.contact_info} onChange={v => setForm(f => ({ ...f, contact_info: v }))} placeholder="+380..." />
          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={save} disabled={!form.name.trim()}>
              {editing ? "Зберегти" : "Додати"}
            </PrimaryBtn>
            <button onClick={() => { setCreating(false); setEditing(null); }}
                    style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Analytics tab ──────────────────────────────────────────────────────────

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(null);  // row being sent to FF
  const [transferQty, setTransferQty] = useState("");
  const [ffDays, setFfDays] = useState(() => parseInt(localStorage.getItem("ff_target_days") || "5", 10) || 5);

  const load = useCallback((days) => {
    setLoading(true);
    apiFetch(`/api/warehouse/analytics?ff_target_days=${days}`)
      .then(r => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(ffDays); }, [load]); // eslint-disable-line

  // Re-fetch when FF target days changes (debounced)
  useEffect(() => {
    localStorage.setItem("ff_target_days", String(ffDays));
    const t = setTimeout(() => load(ffDays), 400);
    return () => clearTimeout(t);
  }, [ffDays, load]);

  const doTransfer = async () => {
    const qty = parseInt(transferQty, 10);
    if (!qty || qty <= 0 || !transferring || !transferring.source_warehouse) return;
    const res = await apiFetch("/api/warehouse/transfer-to-ff", {
      method: "POST",
      body: JSON.stringify({ product_id: transferring.id, warehouse_id: transferring.source_warehouse.id, qty }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({})); alert(e.detail || "Не вдалось перемістити"); }
    setTransferring(null); setTransferQty(""); load(ffDays);
  };

  const doExport = () => {
    if (!data?.reorder_list?.length) return;
    exportXlsx([{
      name: "Список закупівлі",
      rows: [
        ["Назва", "Артикул", "Свій", "FF", "Всього", "В дорозі", "Терм. постач. (дн)", "Темп/день", "Днів до нуля", "Рекомендована к-сть", "Собівартість", "Терміновість"],
        ...data.reorder_list.map(r => [
          r.name, r.article || "", r.own_stock, r.ff_stock, r.total_stock, r.incoming || 0, r.lead_time_days,
          r.velocity, r.days_to_zero ?? "—", r.recommended_qty,
          r.cost_price, r.urgency === "now" ? "ЗАМОВ ЗАРАЗ" : "Скоро",
        ]),
      ],
    }], "список_закупівлі.xlsx");
  };

  if (loading) return <div style={{ color: C.text3, fontSize: 13, padding: "40px 0", textAlign: "center" }}>Завантаження...</div>;
  if (!data) return null;

  const { kpis, reorder_list } = data;
  const transfer_list = data.transfer_list || [];

  const KPI = ({ label, value, sub, color = C.text1 }) => (
    <div style={{ ...S.card, flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const nowList = reorder_list.filter(r => r.urgency === "now");
  const soonList = reorder_list.filter(r => r.urgency === "soon");

  const ReorderRow = ({ r }) => {
    const isNow = r.urgency === "now";
    return (
      <tr onMouseEnter={e => e.currentTarget.style.background = C.bg2}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        <td style={{ ...S.td, paddingLeft: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {r.image_url && <Img src={r.image_url} style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 5, flexShrink: 0 }} />}
            <div>
              <ProdLink name={r.name} productId={r.rozetka_id} style={{ fontSize: 12, maxWidth: 260 }} />
              {r.article && <div style={{ fontSize: 11, color: C.text3 }}>{r.article}</div>}
            </div>
          </div>
        </td>
        <td style={{ ...S.td, textAlign: "right", fontSize: 12 }}>
          <span style={S.badge((r.total_stock ?? r.own_stock) <= 0 ? C.red : C.yellow)}>{r.total_stock ?? r.own_stock}</span>
          <div style={{ fontSize: 10, color: C.text4, marginTop: 2 }}>свій {r.own_stock} · FF {r.ff_stock}</div>
        </td>
        <td style={{ ...S.td, textAlign: "right", fontSize: 12 }}>
          {r.incoming > 0 ? <span style={{ ...S.badge(C.blue), fontSize: 11 }}>{r.incoming}</span> : <span style={{ color: C.text4 }}>—</span>}
        </td>
        <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: C.text2 }}>{r.lead_time_days} дн</td>
        <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: C.text2 }}>{r.velocity}</td>
        <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: r.days_to_zero != null && r.days_to_zero <= r.lead_time_days ? C.red : C.yellow }}>
          {r.days_to_zero != null ? r.days_to_zero : "—"}
        </td>
        <td style={{ ...S.td, textAlign: "right" }}>
          <span style={S.badge(isNow ? C.red : C.yellow, isNow ? `${C.red}20` : `${C.yellow}20`)}>
            {r.recommended_qty} шт
          </span>
        </td>
        <td style={{ ...S.td, textAlign: "right", paddingRight: 12, fontSize: 12, color: C.text2 }}>
          {isNow
            ? <span style={{ color: C.red, fontWeight: 700 }}>ЗАМОВ ЗАРАЗ</span>
            : <span style={{ color: C.yellow }}>Скоро</span>}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* KPI row */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <KPI
          label="Заморожений капітал"
          value={kpis.frozen_capital > 0 ? `${kpis.frozen_capital.toLocaleString("uk")} ₴` : "—"}
          sub="собівартість × (свій + FF)"
          color={C.yellow}
        />
        <KPI
          label="Оборотність (разів/рік)"
          value={kpis.turnover_ratio ?? "—"}
          sub="більше = краще"
          color={kpis.turnover_ratio != null && kpis.turnover_ratio >= 6 ? C.green : C.text1}
        />
        <KPI
          label="Середній DIO"
          value={kpis.portfolio_dio != null ? `${kpis.portfolio_dio} дн` : "—"}
          sub="днів запасу в портфелі"
          color={kpis.portfolio_dio != null && kpis.portfolio_dio > 60 ? C.yellow : C.text1}
        />
        <KPI label="Замов зараз" value={kpis.reorder_now} color={kpis.reorder_now > 0 ? C.red : C.text1} />
        <KPI label="Замов скоро" value={kpis.reorder_soon} color={kpis.reorder_soon > 0 ? C.yellow : C.text1} />
        <KPI label="Відправити на ФФ" value={kpis.transfer_ff ?? 0} color={kpis.transfer_ff > 0 ? C.blue : C.text1} />
      </div>

      {/* Reorder list */}
      {reorder_list.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: C.text3, fontSize: 13, padding: "40px 20px" }}>
          Все добре — жодного товару не потребує закупівлі
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>
              Список закупівлі
              <span style={{ marginLeft: 8, fontSize: 12, color: C.text3, fontWeight: 400 }}>
                {reorder_list.length} позицій
              </span>
            </div>
            <button onClick={doExport} style={{ ...S.btn(false), padding: "6px 14px", fontSize: 12 }}>
              ↓ Excel
            </button>
          </div>
          <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th(), paddingLeft: 12 }}>Товар</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Залишок</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>В дорозі</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Поставка</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Темп/день</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Днів до нуля</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Замовити</th>
                  <th style={{ ...S.th(), textAlign: "right", paddingRight: 12 }}>Статус</th>
                </tr>
              </thead>
              <tbody>
                {nowList.length > 0 && (
                  <tr><td colSpan={8} style={{ ...S.td, background: `${C.red}10`, color: C.red, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", paddingLeft: 12 }}>
                    Критично — замов зараз ({nowList.length})
                  </td></tr>
                )}
                {nowList.map(r => <ReorderRow key={r.id} r={r} />)}
                {soonList.length > 0 && (
                  <tr><td colSpan={8} style={{ ...S.td, background: `${C.yellow}10`, color: C.yellow, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", paddingLeft: 12 }}>
                    Планова закупівля ({soonList.length})
                  </td></tr>
                )}
                {soonList.map(r => <ReorderRow key={r.id} r={r} />)}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: C.text4, marginTop: 8 }}>
            Рекомендована к-сть = темп × (термін поставки + 7 днів буфер) − загальний залишок (свій + FF)
          </div>
        </div>
      )}

      {/* FF transfer controls + list */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text1 }}>
            Відправити на ФФ
            <span style={{ marginLeft: 8, fontSize: 12, color: C.text3, fontWeight: 400 }}>
              {transfer_list.length} позицій — FF спустошується, на своєму складі є запас
            </span>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.text3 }}>
            Заповнювати ФФ на
            <input type="number" min={1} max={365} value={ffDays}
              onChange={e => setFfDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1)))}
              style={{ ...S.input, width: 64, padding: "4px 8px", fontSize: 12, textAlign: "center" }} />
            днів
          </div>
        </div>
        {transfer_list.length === 0 ? (
          <div style={{ ...S.card, textAlign: "center", color: C.text3, fontSize: 13, padding: "30px 20px" }}>
            Немає позицій для відправки на ФФ
          </div>
        ) : (
        <div>
          <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...S.th(), paddingLeft: 12 }}>Товар</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>FF залишок</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Свій залишок</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Темп/день</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Днів FF</th>
                  <th style={{ ...S.th(), textAlign: "right" }}>Відправити</th>
                  <th style={{ ...S.th(), textAlign: "right", paddingRight: 12 }} />
                </tr>
              </thead>
              <tbody>
                {transfer_list.map(r => (
                  <tr key={r.id}
                      onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ ...S.td, paddingLeft: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {r.image_url && <Img src={r.image_url} style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 5, flexShrink: 0 }} />}
                        <div>
                          <ProdLink name={r.name} productId={r.rozetka_id} style={{ fontSize: 12, maxWidth: 260 }} />
                          <div style={{ display: "flex", gap: 6 }}>
                            {r.article && <span style={{ fontSize: 11, color: C.text3 }}>{r.article}</span>}
                            {r.no_sales && <span style={{ fontSize: 10, color: C.text4 }}>без продажів</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...S.td, textAlign: "right", fontSize: 12 }}>
                      <span style={S.badge(r.ff_stock <= 0 ? C.red : C.yellow)}>{r.ff_stock}</span>
                    </td>
                    <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: C.text2 }}>{r.own_stock}</td>
                    <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: C.text2 }}>{r.no_sales ? "—" : r.velocity}</td>
                    <td style={{ ...S.td, textAlign: "right", fontSize: 12, color: r.ff_days_cover != null && r.ff_days_cover <= 2 ? C.red : C.yellow }}>{r.ff_days_cover != null ? r.ff_days_cover : "—"}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      {r.recommended_transfer != null
                        ? <span style={S.badge(C.blue, `${C.blue}20`)}>{r.recommended_transfer} шт</span>
                        : <span style={{ color: C.text4, fontSize: 12 }}>вручну</span>}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", paddingRight: 12 }}>
                      <button onClick={() => { setTransferring(r); setTransferQty(r.recommended_transfer != null ? String(r.recommended_transfer) : ""); }}
                              disabled={!r.source_warehouse}
                              title={r.source_warehouse ? `Зі складу «${r.source_warehouse.name}»` : "Немає складу-джерела"}
                              style={{ ...S.btn(false), padding: "4px 10px", fontSize: 11, color: C.blue }}>
                        → ФФ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: C.text4, marginTop: 8 }}>
            Ціль ФФ = темп × {ffDays} днів. Після відправки ff_stock підніметься після наступної синхронізації з Rozetka.
          </div>
        </div>
        )}
      </div>

      {transferring && (
        <Modal title={`Відправити на ФФ: ${transferring.name?.slice(0, 40)}`} onClose={() => { setTransferring(null); setTransferQty(""); }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: C.text2 }}>
            Зі складу: <strong style={{ color: C.text1 }}>{transferring.source_warehouse?.name || "—"}</strong>
            <span style={{ color: C.text3 }}> (наявно {transferring.own_stock})</span>
          </div>
          <Input label="Кількість на ФФ" type="number" value={transferQty} onChange={setTransferQty} />
          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={doTransfer} disabled={!transferQty || parseInt(transferQty, 10) <= 0 || parseInt(transferQty, 10) > transferring.own_stock} color={C.blue}>Відправити</PrimaryBtn>
            <button onClick={() => { setTransferring(null); setTransferQty(""); }} style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ── Warehouses tab ───────────────────────────────────────────────────────────

function WarehousesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/warehouse/warehouses");
      if (res.ok) { const d = await res.json(); setList(d.warehouses || []); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim()) return;
    await apiFetch("/api/warehouse/warehouses", { method: "POST", body: JSON.stringify({ name: newName.trim() }) });
    setNewName(""); setCreating(false); load();
  };
  const rename = async () => {
    if (!editName.trim() || !editing) return;
    await apiFetch(`/api/warehouse/warehouses/${editing.id}`, { method: "PATCH", body: JSON.stringify({ name: editName.trim() }) });
    setEditing(null); setEditName(""); load();
  };
  const remove = async (w) => {
    if (!window.confirm(`Видалити склад «${w.name}»?`)) return;
    const res = await apiFetch(`/api/warehouse/warehouses/${w.id}`, { method: "DELETE" });
    if (!res.ok) { const e = await res.json().catch(() => ({})); alert(e.detail || "Не вдалось видалити"); }
    load();
  };
  const makeDefault = async (w) => {
    await apiFetch(`/api/warehouse/warehouses/${w.id}/make-default-fbs`, { method: "POST" });
    load();
  };

  if (loading) return <div style={{ color: C.text3, fontSize: 13, padding: "40px 0", textAlign: "center" }}>Завантаження...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.text3 }}>
          FBS-замовлення списуються з основного складу (★). FF — системний склад Rozetka.
        </div>
        <PrimaryBtn onClick={() => setCreating(true)}>+ Склад</PrimaryBtn>
      </div>

      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...S.th(), paddingLeft: 16 }}>Назва</th>
              <th style={{ ...S.th() }}>Тип</th>
              <th style={{ ...S.th(), textAlign: "right" }}>Залишок (од.)</th>
              <th style={{ ...S.th(), textAlign: "right", paddingRight: 16 }} />
            </tr>
          </thead>
          <tbody>
            {list.map(w => (
              <tr key={w.id}>
                <td style={{ ...S.td, paddingLeft: 16, fontSize: 13, color: C.text1, fontWeight: 500 }}>
                  {w.is_default_fbs && <span title="Основний для FBS" style={{ color: C.yellow, marginRight: 6 }}>★</span>}
                  {w.name}
                </td>
                <td style={{ ...S.td }}>
                  {w.kind === "ff"
                    ? <span style={{ ...S.badge(C.blue), fontSize: 11 }}>FF Rozetka</span>
                    : <span style={{ ...S.badge(C.text3), fontSize: 11 }}>Свій</span>}
                </td>
                <td style={{ ...S.td, textAlign: "right", fontSize: 13, color: C.text2 }}>{fmt(w.total_qty)}</td>
                <td style={{ ...S.td, textAlign: "right", paddingRight: 16 }}>
                  {w.kind === "own" && (
                    <div style={{ display: "inline-flex", gap: 6 }}>
                      {!w.is_default_fbs && (
                        <button onClick={() => makeDefault(w)} style={{ ...S.btn(false), padding: "4px 10px", fontSize: 11 }}>Зробити основним</button>
                      )}
                      <button onClick={() => { setEditing(w); setEditName(w.name); }} style={{ ...S.btn(false), padding: "4px 10px", fontSize: 11 }}>✎</button>
                      <button onClick={() => remove(w)} style={{ ...S.btn(false), padding: "4px 10px", fontSize: 11, color: C.red }}>🗑</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={4} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>
                Немає складів. Створіть перший свій склад.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <Modal title="Новий склад" onClose={() => { setCreating(false); setNewName(""); }}>
          <Input label="Назва складу" value={newName} onChange={setNewName} placeholder="Напр.: Склад Печерськ" />
          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={create} disabled={!newName.trim()}>Створити</PrimaryBtn>
            <button onClick={() => { setCreating(false); setNewName(""); }} style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
      {editing && (
        <Modal title="Перейменувати склад" onClose={() => { setEditing(null); setEditName(""); }}>
          <Input label="Назва складу" value={editName} onChange={setEditName} />
          <div style={{ display: "flex", gap: 10 }}>
            <PrimaryBtn onClick={rename} disabled={!editName.trim()}>Зберегти</PrimaryBtn>
            <button onClick={() => { setEditing(null); setEditName(""); }} style={{ ...S.btn(false), padding: "9px 20px", fontSize: 13 }}>Скасувати</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Movements tab ────────────────────────────────────────────────────────────

const REASON_LABEL = {
  purchase: "Закупка",
  manual: "Коригування",
  sale: "Продаж",
  sale_reversal: "Повернення",
  transfer_to_ff: "На ФФ",
  import: "Імпорт",
};
const REASON_COLOR = {
  purchase: "#22c55e", manual: "#eab308", sale: "#ef4444",
  sale_reversal: "#3b82f6", transfer_to_ff: "#3b82f6", import: "#a855f7",
};

function MovementsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 50;

  const load = useCallback(async (p = 1, rsn = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: PAGE_SIZE });
      if (rsn) params.set("reason", rsn);
      const res = await apiFetch(`/api/warehouse/movements?${params}`);
      if (res.ok) { const d = await res.json(); setItems(d.items || []); setTotal(d.total || 0); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page, reason); }, [page, reason, load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const fmtDt = (iso) => { if (!iso) return ""; const [d, t] = iso.split("T"); return `${d} ${(t || "").slice(0, 5)}`; };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <select value={reason} onChange={e => { setPage(1); setReason(e.target.value); }}
          style={{ ...S.input, width: 200, padding: "6px 10px", fontSize: 13 }}>
          <option value="">Всі типи</option>
          {Object.entries(REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span style={{ color: C.text3, fontSize: 12, marginLeft: "auto" }}>{total} рухів</span>
      </div>

      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...S.th(), paddingLeft: 16 }}>Дата</th>
              <th style={{ ...S.th() }}>Товар</th>
              <th style={{ ...S.th() }}>Склад</th>
              <th style={{ ...S.th() }}>Тип</th>
              <th style={{ ...S.th(), textAlign: "right" }}>Зміна</th>
              <th style={{ ...S.th(), paddingRight: 16 }}>Примітка</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>Завантаження...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: C.text3, padding: "40px 0" }}>Немає рухів</td></tr>}
            {items.map(m => (
              <tr key={m.id}
                  onMouseEnter={e => e.currentTarget.style.background = C.bg2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ ...S.td, paddingLeft: 16, fontSize: 12, color: C.text3, whiteSpace: "nowrap" }}>{fmtDt(m.created_at)}</td>
                <td style={{ ...S.td }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    {m.image_url && <Img src={m.image_url} style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 5, flexShrink: 0 }} />}
                    {m.rozetka_id || m.product_id
                      ? <ProdLink name={m.product_name || `#${m.product_id}`} productId={m.rozetka_id} style={{ fontSize: 12, fontWeight: 400, maxWidth: 280 }} />
                      : <span style={{ fontSize: 12, color: C.text1 }}>{m.product_name || `#${m.product_id}`}</span>}
                  </div>
                </td>
                <td style={{ ...S.td, fontSize: 12, color: C.text2 }}>{m.warehouse_name || (m.warehouse_id ? `#${m.warehouse_id}` : "—")}</td>
                <td style={{ ...S.td }}>
                  <span style={{ ...S.badge(REASON_COLOR[m.reason] || C.text3), fontSize: 11 }}>{REASON_LABEL[m.reason] || m.reason}</span>
                </td>
                <td style={{ ...S.td, textAlign: "right", fontSize: 13, fontWeight: 700, color: m.delta >= 0 ? C.green : C.red }}>
                  {m.delta >= 0 ? "+" : ""}{m.delta}
                </td>
                <td style={{ ...S.td, paddingRight: 16, fontSize: 12, color: C.text3, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.note || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={S.btn(false)}>← Назад</button>
          <span style={{ color: C.text3, fontSize: 13, alignSelf: "center" }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={S.btn(false)}>Вперед →</button>
        </div>
      )}
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────

export default function WarehousePage() {
  const [tab, setTab] = useState("stock");

  const tabs = [
    { id: "stock", label: "📦 Запаси" },
    { id: "warehouses", label: "🏢 Склади" },
    { id: "purchases", label: "📥 Надходження" },
    { id: "movements", label: "🔄 Рухи" },
    { id: "suppliers", label: "🏭 Постачальники" },
    { id: "analytics", label: "📊 Аналітика" },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text1, margin: "0 0 4px" }}>Склад</h2>
        <div style={{ fontSize: 13, color: C.text3 }}>Управління запасами, надходженнями та собівартістю</div>
      </div>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "stock" && <StockTab />}
      {tab === "warehouses" && <WarehousesTab />}
      {tab === "purchases" && <PurchasesTab />}
      {tab === "movements" && <MovementsTab />}
      {tab === "suppliers" && <SuppliersTab />}
      {tab === "analytics" && <AnalyticsTab />}
    </div>
  );
}
